import type { RetornoValidacao } from '@/lib/presencial';

interface Props {
  resultado: RetornoValidacao;
  fotoUrl: string | null;
  aoFechar: () => void;
}

interface Estilo {
  cor: string;
  icone: string;
  titulo: string;
  claro?: boolean;
}

// Cor, ícone e título grande conforme o resultado da bipagem.
function estiloDe(r: RetornoValidacao): Estilo {
  switch (r.situacao) {
    case 'vaga':
      return { cor: 'bg-success-600', icone: 'ri-check-line', titulo: `VAGA nº ${r.ordem ?? ''}` };
    case 'lista_espera':
      return {
        cor: 'bg-warning-500',
        icone: 'ri-time-line',
        titulo: `LISTA DE ESPERA · ${r.posicao_espera ?? ''}º`,
        claro: true,
      };
    case 'ja_validada':
      return {
        cor: 'bg-foreground-700',
        icone: 'ri-check-double-line',
        titulo: `Já validada · nº ${r.ordem ?? ''}`,
      };
    case 'cancelada':
      return { cor: 'bg-danger-600', icone: 'ri-close-circle-line', titulo: 'CANCELADA' };
    case 'nao_presencial':
      return {
        cor: 'bg-danger-600',
        icone: 'ri-error-warning-line',
        titulo: 'NÃO É PRESENCIAL',
      };
    case 'sem_senha':
      return {
        cor: 'bg-info-100',
        icone: 'ri-information-line',
        titulo: 'FICHA PELA SENHA',
        claro: true,
      };
    default:
      return { cor: 'bg-danger-600', icone: 'ri-question-line', titulo: 'NÃO ENCONTRADA' };
  }
}

// Cartão grande do resultado do bipe: fica 4 s na tela e some sozinho.
export default function ResultadoValidacaoCard({ resultado, fotoUrl, aoFechar }: Props) {
  const est = estiloDe(resultado);
  const mostraPessoa = resultado.situacao === 'vaga' || resultado.situacao === 'lista_espera';
  const mostraFoto = mostraPessoa && !!fotoUrl;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={aoFechar}>
      <div className="absolute inset-0 bg-black/45" aria-hidden />
      <div
        className={`animate-modal-pop relative z-10 w-full max-w-[480px] overflow-hidden rounded-[24px] ${est.cor} ${
          est.claro ? 'text-warning-950' : 'text-white'
        }`}
      >
        <div className="flex flex-col items-center px-6 pb-6 pt-7 text-center">
          <span
            className={`flex h-20 w-20 items-center justify-center rounded-full ${
              est.claro ? 'bg-black/10' : 'bg-white/20'
            }`}
          >
            <i className={`${est.icone} text-[48px]`} aria-hidden />
          </span>

          <h2 className="mt-4 text-[28px] font-extrabold uppercase leading-none tracking-tight">
            {est.titulo}
          </h2>

          {mostraPessoa && resultado.nome && (
            <p className="mt-4 text-[22px] font-bold leading-tight">{resultado.nome}</p>
          )}

          {mostraPessoa && (
            <div className="mt-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
              {resultado.idade != null && (
                <p className={`text-[15px] ${est.claro ? 'text-warning-900' : 'text-white/85'}`}>
                  {resultado.idade} anos
                </p>
              )}
              {resultado.grupo && (
                <p className={`text-[15px] ${est.claro ? 'text-warning-900' : 'text-white/75'}`}>
                  {resultado.grupo}
                </p>
              )}
              {resultado.modalidade && !resultado.grupo && (
                <p className={`text-[15px] ${est.claro ? 'text-warning-900' : 'text-white/75'}`}>
                  {resultado.modalidade}
                </p>
              )}
            </div>
          )}

          {mostraFoto && (
            <div
              className={`mt-4 flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-4 ${
                est.claro ? 'border-black/10 bg-black/5' : 'border-white/40 bg-white/10'
              }`}
            >
              <img src={fotoUrl as string} alt={resultado.nome ?? 'Participante'} className="h-full w-full object-cover" />
            </div>
          )}

          {!mostraPessoa && resultado.mensagem && (
            <p className={`mt-3 max-w-sm text-[15px] ${est.claro ? 'text-warning-900' : 'text-white/90'}`}>
              {resultado.mensagem}
            </p>
          )}

          <button
            type="button"
            onClick={aoFechar}
            className={`aperta mt-6 w-full cursor-pointer whitespace-nowrap rounded-[18px] border-2 px-6 py-3 text-[16px] font-semibold ${
              est.claro ? 'border-black/25 text-warning-950' : 'border-white/60 text-white'
            }`}
          >
            Próximo
          </button>
        </div>
      </div>
    </div>
  );
}